import {
  ECareerLandingPageTabs,
  ECommonContentLayout,
  thunkActionLoading,
} from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector } from '@redux'
import { message } from 'antd'
import { t } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ECareerBannerType } from 'src/interfaces/career-landing-management'
import { IMediaItem } from 'src/interfaces/gallery'
import {
  createCareerBannerBottomAction,
  getCareerBannerBottomAction,
} from 'src/redux/actions/career-landing-management'
import { bannerBottomSchema, IBannerBottom } from './bannerSchema'

interface IAboutUsPageTabItemProps {
  activeTab?: string
}

export const useBannerBottom = ({ activeTab }: IAboutUsPageTabItemProps) => {
  const [isModalLoading, setIsModalLoading] = useState(false)
  const dispatch = useAppDispatch()

  const [isChange, setIsChange] = useState(false)

  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<
    'desktop' | 'mobile' | null
  >(null)
  const onChangeDesktopRef = useRef<any>(null)
  const onChangeMobileRef = useRef<any>(null)

  const createBannerBottomActionLoading = useAppSelector(
    (state) =>
      state.careerLanding.loadings[
      thunkActionLoading.CREATE_BANNER_BOTTOM_LOADING
      ]
  )
  const getBannerBottomActionLoading = useAppSelector(
    (state) =>
      state.careerLanding.loadings[thunkActionLoading.GET_BANNER_BOTTOM_LOADING]
  )

  const bannerBottom = useAppSelector(
    (state) => state.careerLanding.bannerBottom
  )

  const [imageSrc, setImageSrc] = useState(bannerBottom?.media?.url ?? '')
  const [imageMobileSrc, setImageMobileSrc] = useState(
    bannerBottom?.mediaMobile?.url ?? ''
  )

  const isDisabledSaveButton = useMemo(() => {
    return createBannerBottomActionLoading || false
  }, [createBannerBottomActionLoading])

  const { handleSubmit, control, setValue, getValues } = useForm<IBannerBottom>(
    {
      defaultValues: {
        content: '',
        contentInDutch: '',
        mediaId: undefined,
        mediaMobileId: undefined,
        textColor: '',
        layout: ECommonContentLayout.LAYOUT_ONE,
        type: ECareerBannerType.bottom,
      },
      resolver: zodResolver(bannerBottomSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
    }
  )

  const onSubmit = async (data: IBannerBottom) => {
    try {
      const responseCreateBannerBottom = await dispatch(
        createCareerBannerBottomAction(data as any)
      ).unwrap()

      if (responseCreateBannerBottom.success) {
        message.success({
          content:
            responseCreateBannerBottom?.message ?? t('common:save_success'),
        })
      }
    } catch (error: any) {
      message.error({
        content:
          error?.message ?? t('error:some_thing_went_wrong_please_try_again'),
      })
    }
  }

  const onSave = () => {
    handleSubmit(onSubmit)()
    setIsChange(false)
  }

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'desktop') {
      setImageSrc(data?.baseUrl ?? '')
      setValue('mediaId', data?.id ?? 0)
      onChangeDesktopRef.current(data?.id ?? 0)
    } else if (selectedField === 'mobile') {
      setImageMobileSrc(data?.baseUrl ?? '')
      setValue('mediaMobileId', data?.id ?? 0)
      onChangeMobileRef.current(data?.id ?? 0)
    }

    setShowModalSelectMedia(false)
    setIsChange(true)
  }

  useEffect(() => {
    if (activeTab === ECareerLandingPageTabs.BANNER) {
      const getBannerBottom = async () => {
        try {
          const responseGetLanding = await dispatch(
            getCareerBannerBottomAction(ECareerBannerType.bottom)
          ).unwrap()

          if (responseGetLanding.success) {
            setIsChange(false)
          }
        } catch (error: any) {
          message.error({
            content:
              error.message ??
              t('error:some_thing_went_wrong_please_try_again'),
          })
        }
      }
      getBannerBottom()
    }
  }, [activeTab, dispatch, setValue])

  useEffect(() => {
    if (bannerBottom) {
      if (Object.keys(bannerBottom).length) {
        setValue('content', bannerBottom?.content)
        setValue('contentInDutch', bannerBottom?.contentInDutch)
        setValue('mediaId', bannerBottom?.media?.id)
        setValue('mediaMobileId', bannerBottom?.mediaMobile?.id)
        setValue('textColor', bannerBottom?.metaData?.textColor)
        setValue('layout', bannerBottom?.metaData?.layout.toString())
        setImageSrc(bannerBottom?.media?.url ?? '')
        setImageMobileSrc(bannerBottom?.mediaMobile?.url ?? '')
      }
    }
  }, [bannerBottom])
  return {
    getBannerBottomActionLoading,
    control,
    setIsChange,
    onChangeDesktopRef,
    setValue,
    setImageSrc,
    setSelectedField,
    setShowModalSelectMedia,
    imageSrc,
    getValues,
    isModalLoading,
    setIsModalLoading,
    onChangeMobileRef,
    isDisabledSaveButton,
    imageMobileSrc,
    onSave,
    isChange,
    showModalSelectMedia,
    onCloseModalSelectMedia,
    onSelectMedia,
  }
}
